import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Paper,
  TextField,
  Alert,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import useApiEp from "../../hooks/useApiEp";
import PhoneIcon from "@mui/icons-material/Phone";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Link, useParams } from "react-router-dom";
import {
  getHeader,
  launchEsSignup,
  roundToMaxDecimals,
  saveToken,
  toastError,
} from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import FacebookIcon from "@mui/icons-material/Facebook";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SecurityIcon from "@mui/icons-material/Security";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import axios from "axios";
import { API_ENDPOINT, RCS_PARTNERS } from "../../utils/constants";
import MigrateChatBot from "./MigrateChatBot";
import PlanDisplay from "./PlanDisplay";
import { toast } from "react-toastify";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import LoginIcon from "@mui/icons-material/Login";
import { Settings2Icon, Users, CalendarDays } from "lucide-react";
import TableChartIcon from "@mui/icons-material/TableChart";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

// Helper function to format dates to "17 Jul 2026"
const formatExpiryDate = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SecretValue = ({ label, value }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasValue = Boolean(value);

  const handleCopy = async () => {
    if (!hasValue) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      toast.error(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        minHeight: 48,
        px: 1.5,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderRadius: 2,
        bgcolor: "grey.50",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: "monospace",
          fontSize: "0.78rem",
          fontWeight: isVisible ? 500 : 700,
          letterSpacing: isVisible ? 0 : "0.14em",
          overflowWrap: "anywhere",
          color: hasValue ? "text.primary" : "text.secondary",
        }}
      >
        {!hasValue ? "Not available" : isVisible ? value : "••••••••••••••••"}
      </Typography>

      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip
          title={isVisible ? `Hide ${label.toLowerCase()}` : `View ${label.toLowerCase()}`}
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={!hasValue}
              onClick={() => setIsVisible((visible) => !visible)}
              aria-label={
                isVisible
                  ? `Hide ${label.toLowerCase()}`
                  : `View ${label.toLowerCase()}`
              }
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              {isVisible ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={copied ? "Copied" : `Copy ${label.toLowerCase()}`} arrow>
          <span>
            <IconButton
              size="small"
              disabled={!hasValue}
              onClick={handleCopy}
              aria-label={`Copy ${label.toLowerCase()}`}
              color={copied ? "success" : "primary"}
              sx={{
                border: "1px solid",
                borderColor: copied ? "success.light" : "divider",
                bgcolor: "background.paper",
              }}
            >
              {copied ? (
                <CheckRoundedIcon fontSize="small" />
              ) : (
                <ContentCopyOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

const AccountDetails = () => {
  const { id } = useParams();

  const [marking, setMarking] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [updatingQualityBypass, setUpdatingQualityBypass] = useState(false);

  const [rcsTab, setRcsTab] = useState(RCS_PARTNERS.JIO);
  const [period, setPeriod] = useState("last_month");

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-account/${id}`
  );

  const { data: uniquePhoneData, isLoading: isLoadingPhones } = useApiEp(
    `/message/unique-phone-numbers?period=${period}&cid=${data?.customerId}`,
    !!data?.customerId
  );

  const handleQualityBypassChange = useCallback(
    async (bypassNumberQuality) => {
      if (!data?._id) {
        toast.error("Account details are not available yet.");
        return;
      }

      try {
        setUpdatingQualityBypass(true);

        const response = await axios.patch(
          `${API_ENDPOINT}/root/update-quality-bypass-settings/${data._id}`,
          { bypassNumberQuality },
          getHeader()
        );

        toast.success(
          response.data.message ||
            `Number quality bypass ${
              bypassNumberQuality ? "enabled" : "disabled"
            }.`
        );

        await refetch();
      } catch (requestError) {
        toast.error(
          requestError?.response?.data?.message ||
            "Could not update the number quality setting. Please try again."
        );
      } finally {
        setUpdatingQualityBypass(false);
      }
    },
    [data?._id, refetch]
  );

  // ─── RCS Credentials Modal ────────────────────────────────────────────────
  const [openRCSModal, setOpenRCSModal] = useState(false);
  const [rcsBotId, setRcsBotId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ─── RCS Wallet Expiry Modal ─────────────────────────────────────────────
  const [openRcsExpiryModal, setOpenRcsExpiryModal] = useState(false);
  const [rcsExpiryDate, setRcsExpiryDate] = useState(null);
  const [updatingRcsExpiry, setUpdatingRcsExpiry] = useState(false);

  // ─── Conversion Rates Modal ───────────────────────────────────────────────
  const [openRatesModal, setOpenRatesModal] = useState(false);

  const [ratesForm, setRatesForm] = useState({
    voiceConversionRate: "",
    rcsConversionRate: "",
  });

  const [updatingRates, setUpdatingRates] = useState(false);

  // ─── Google Sheet Integration Modal ──────────────────────────────────────
  const [openGSheetModal, setOpenGSheetModal] = useState(false);

  const [gSheetForm, setGSheetForm] = useState({
    wabaChats: true,
    voiceReport: true,
    rcsReport: true,
  });

  const [savingGSheet, setSavingGSheet] = useState(false);

  // ─── Release Locked Voice Wallet Modal ───────────────────────────────────
  const [openReleaseLockModal, setOpenReleaseLockModal] = useState(false);

  const [releaseLockForm, setReleaseLockForm] = useState({
    mode: "full",
    amount: "",
  });

  const [releasingLock, setReleasingLock] = useState(false);

  // ─── RCS Wallet Expiry Handlers ──────────────────────────────────────────
  const handleOpenRcsExpiryModal = useCallback(() => {
    setRcsExpiryDate(null);
    setOpenRcsExpiryModal(true);
  }, []);

  const handleCloseRcsExpiryModal = useCallback(() => {
    if (updatingRcsExpiry) return;

    setOpenRcsExpiryModal(false);
    setRcsExpiryDate(null);
  }, [updatingRcsExpiry]);

  const handleUpdateRcsExpiry = useCallback(async () => {
    if (!rcsExpiryDate || !dayjs(rcsExpiryDate).isValid()) {
      toastError("Please select a valid expiry date");
      return;
    }

    try {
      setUpdatingRcsExpiry(true);

      const response = await axios.patch(
        `${API_ENDPOINT}/root/update-rcs-wallet-expiry/${id}`,
        {
          expiryDate: dayjs(rcsExpiryDate).format("YYYY-MM-DD"),
        },
        getHeader()
      );

      toast.success(
        response.data.message || "RCS wallet expiry updated successfully"
      );

      setOpenRcsExpiryModal(false);
      setRcsExpiryDate(null);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setUpdatingRcsExpiry(false);
    }
  }, [id, rcsExpiryDate, refetch]);

  // ─── Release Locked Wallet ────────────────────────────────────────────────
  const handleOpenReleaseLockModal = useCallback(() => {
    setReleaseLockForm({
      mode: "full",
      amount: "",
    });

    setOpenReleaseLockModal(true);
  }, []);

  const handleReleaseLockSubmit = useCallback(async () => {
    const lockedAmount = Number(data?.lockedVoiceCallWallet ?? 0);

    let payload = {};

    if (releaseLockForm.mode === "full") {
      if (!lockedAmount || lockedAmount <= 0) {
        toastError("No locked amount available to release");
        return;
      }

      payload = {
        releaseFullAmount: true,
      };
    } else {
      const enteredAmount = parseFloat(releaseLockForm.amount);

      if (isNaN(enteredAmount) || enteredAmount <= 0) {
        toastError("Enter a valid amount to release");
        return;
      }

      if (enteredAmount > lockedAmount) {
        toastError("Amount cannot be greater than the locked balance");
        return;
      }

      payload = {
        amount: enteredAmount,
      };
    }

    try {
      setReleasingLock(true);

      const response = await axios.post(
        `${API_ENDPOINT}/root/release-locked-voice-wallet/${id}`,
        payload,
        getHeader()
      );

      toast.success(
        response.data.message || "Locked voice wallet amount released"
      );

      setOpenReleaseLockModal(false);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setReleasingLock(false);
    }
  }, [releaseLockForm, id, refetch, data]);

  // ─── Google Sheet Integration ─────────────────────────────────────────────
  const handleOpenGSheetModal = useCallback(() => {
    setGSheetForm({
      wabaChats: data?.gSheet?.wabaChats ?? true,
      voiceReport: data?.gSheet?.voiceReport ?? true,
      rcsReport: data?.gSheet?.rcsReport ?? true,
    });

    setOpenGSheetModal(true);
  }, [data]);

  const handleGSheetSubmit = useCallback(async () => {
    try {
      setSavingGSheet(true);

      const response = await axios.post(
        `${API_ENDPOINT}/root/update-gsheet-integration/${id}`,
        gSheetForm,
        getHeader()
      );

      toast.success(
        response.data.message || "Google Sheet integration updated"
      );

      setOpenGSheetModal(false);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSavingGSheet(false);
    }
  }, [gSheetForm, id, refetch]);

  // ─── Conversion Rates ─────────────────────────────────────────────────────
  const handleOpenRatesModal = useCallback(() => {
    setRatesForm({
      voiceConversionRate: data?.voiceConversionRate ?? "",
      rcsConversionRate: data?.rcsConversionRate ?? "",
    });

    setOpenRatesModal(true);
  }, [data]);

  const handleRatesSubmit = useCallback(async () => {
    const payload = {};

    const voice = parseFloat(ratesForm.voiceConversionRate);
    const rcs = parseFloat(ratesForm.rcsConversionRate);

    if (!isNaN(voice) && ratesForm.voiceConversionRate !== "") {
      payload.voiceConversionRate = voice;
    }

    if (!isNaN(rcs) && ratesForm.rcsConversionRate !== "") {
      payload.rcsConversionRate = rcs;
    }

    if (Object.keys(payload).length === 0) {
      toastError("Enter at least one conversion rate to update");
      return;
    }

    try {
      setUpdatingRates(true);

      const response = await axios.post(
        `${API_ENDPOINT}/root/update-coversion-rates/${id}`,
        payload,
        getHeader()
      );

      toast.success(response.data.message || "Conversion rates updated");

      setOpenRatesModal(false);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setUpdatingRates(false);
    }
  }, [ratesForm, id, refetch]);

  // ─── Toggle Active / Inactive ─────────────────────────────────────────────
  const handleToggleActiveStatus = useCallback(async () => {
    if (!data) return;

    const newStatus = data.status === "active" ? "inactive" : "active";

    try {
      setTogglingStatus(true);

      const encodedCusId = encodeURIComponent(data.customerId);

      const response = await axios.patch(
        `${API_ENDPOINT}/root/update-account-active-status/${encodedCusId}`,
        {
          status: newStatus,
        },
        getHeader()
      );

      toast.success(response.data.message);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setTogglingStatus(false);
    }
  }, [data, refetch]);

  // ─── Account Settings ─────────────────────────────────────────────────────
  const handleAccountSettings = useCallback(
    async (aStatus) => {
      if (!data) return;

      const payload = {};

      if (aStatus === "expiry") {
        payload.expired = !Boolean(data?.expired);
      } else if (aStatus === "mmLite") {
        payload.isMMLite = !Boolean(data?.isMMLite);
      }

      try {
        setMarking(true);

        const response = await axios.post(
          `${API_ENDPOINT}/root/account-settings`,
          {
            settings: payload,
            id,
          },
          getHeader()
        );

        toast.success(response.data.message);

        refetch();
      } catch (error) {
        toastError(error);
      } finally {
        setMarking(false);
      }
    },
    [id, data, refetch]
  );

  // ─── Account Login ────────────────────────────────────────────────────────
  const handleAccountLogin = useCallback(async () => {
    if (!data) return;

    try {
      setLoggingIn(true);

      const response = await axios.post(
        `${API_ENDPOINT}/user/account-login`,
        {
          customerId: data.customerId,
        },
        getHeader()
      );

      saveToken(response.data.token);

      document.location.href = "/home";
    } catch (error) {
      toastError(error);
    } finally {
      setLoggingIn(false);
    }
  }, [data]);

  // ─── RCS Credentials ──────────────────────────────────────────────────────
  const handleOpenRCSModal = useCallback(() => {
    setRcsBotId(data?.rcsBotId || "");
    setRcsTab(RCS_PARTNERS.JIO);
    setOpenRCSModal(true);
  }, [data]);

  const handleRCSSubmit = useCallback(async () => {
    if (!rcsBotId) {
      toastError("RCS ID or Bot Id required");
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API_ENDPOINT}/root/enable-rcs-option2`,
        {
          botId: rcsBotId,
          accountId: id,
          rcsPartner: rcsTab,
        },
        getHeader()
      );

      toast.success(
        response.data.message || "RCS Option 2 configured successfully"
      );

      setOpenRCSModal(false);

      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  }, [rcsBotId, id, rcsTab, refetch]);

  if (isLoading) {
    return (
      <Container
        sx={{
          my: 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 3 }}>
        <Typography color="error">
          Error: {error?.message || "Failed to load account details"}
        </Typography>
      </Container>
    );
  }

  const account = data;

  const isActive = account?.status === "active";
  const normalizedNumberQuality = String(
    account?.numberQuality || ""
  ).toUpperCase();
  const showQualityBypass =
    ["RED", "YELLOW"].includes(normalizedNumberQuality) ||
    account?.bypassNumberQuality === true;
  const isAiSensyPartner =
    String(account?.partner || "")
      .trim()
      .toLowerCase() === "aisensy";

  const sectionCardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 3,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  };

  const InfoSection = ({ title, icon, children, ...props }) => (
    <Box {...props}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          mb: 2,
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              color: "primary.main",
              bgcolor: "rgba(25, 118, 210, 0.08)",
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        )}

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>

        <Divider sx={{ flex: 1 }} />
      </Stack>

      {children}
    </Box>
  );

  const getQualityColor = (quality) => {
    switch (quality) {
      case "HIGH":
      case "GREEN":
        return "success";

      case "MEDIUM":
      case "YELLOW":
        return "warning";

      case "LOW":
      case "RED":
        return "error";

      default:
        return "default";
    }
  };

  const walletItems = [
    {
      label: "AI Wallet",
      value: `${account?.aiWallet ?? 0}`,
      color: "secondary",
      variant: "outlined",
    },
    {
      label: "Waba Wallet",
      value: `₹${roundToMaxDecimals(account?.wallet ?? 0, 2)}`,
      color: "primary",
      variant: "filled",
      expiryDate: account?.walletExpiry,
    },
    {
      label: "RCS Wallet",
      value: `${roundToMaxDecimals(account?.rcsWalletRichMedia ?? 0, 2)}`,
      color: "info",
      variant: "outlined",
      expiryDate: account?.rcsWalletRichMediaExpiry,

      /*
       * Only this wallet gets the Set Expiry action
       * when expiryDate is missing.
       */
      allowSetExpiry: true,
    },
    {
      label: "Voice Wallet",
      value: `${account?.voiceCallWallet ?? 0}`,
      color: "info",
      variant: "outlined",
      expiryDate: account?.voiceCallWalletExpiry,
    },
  ];

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />

      <Container maxWidth="lg">
        <PageTitle sx={{ mt: 2, mb: 1 }}>Account Details</PageTitle>

        <Card sx={{ ...sectionCardSx, mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <PlanDisplay data={account} id={id} />
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack
                  spacing={2}
                  alignItems={{
                    xs: "flex-start",
                    md: "flex-end",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={account?.status?.toUpperCase()}
                      color={isActive ? "success" : "error"}
                      variant="filled"
                      size="medium"
                    />

                    <GlassButtonLoading
                      loading={togglingStatus}
                      onClick={handleToggleActiveStatus}
                      variant="outlined"
                      color={isActive ? "error" : "success"}
                      size="small"
                    >
                      {isActive ? "Mark Inactive" : "Mark Active"}
                    </GlassButtonLoading>
                  </Stack>

                  <GlassButtonLoading
                    loading={marking}
                    onClick={() => handleAccountSettings("expiry")}
                    variant={account?.expired ? "outlined" : "contained"}
                    color={account?.expired ? "success" : "error"}
                    size="small"
                  >
                    {account?.expired ? "Revoke Expiry" : "Mark Expired"}
                  </GlassButtonLoading>

                  <GlassButtonLoading
                    loading={marking}
                    onClick={() => handleAccountSettings("mmLite")}
                    variant={account?.isMMLite ? "outlined" : "contained"}
                    color={account?.isMMLite ? "success" : "error"}
                    size="small"
                  >
                    {account?.isMMLite ? "Unmark MM Lite?" : "Mark MM Lite"}
                  </GlassButtonLoading>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* CUSTOMER INFORMATION */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection
                    title="Customer Information"
                    icon={<PersonIcon />}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Customer ID
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body1" fontWeight={500}>
                            {account.customerId}
                          </Typography>

                          <Chip
                            icon={<Users size={16} />}
                            label={`${
                              account.accounts?.length || 0
                            } Sub-accounts`}
                            size="small"
                            clickable
                            onClick={() =>
                              window.open(
                                `/home/${account.customerId}/sub-accounts`,
                                "_blank"
                              )
                            }
                            sx={{
                              borderRadius: "8px",
                              fontWeight: 500,
                              cursor: "pointer",

                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: 1,
                              },

                              transition: "all 0.2s ease",
                            }}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Verified Name
                        </Typography>

                        <Typography variant="body1" fontWeight="medium">
                          {account.verifiedName}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Contact Number
                        </Typography>

                        <Typography variant="body1">
                          {account.contactNumber || "NA"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Email ID
                        </Typography>

                        <Typography variant="body1">
                          {account.contactEmail || "NA"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Created On
                        </Typography>

                        <Typography variant="body1">
                          {account.createdOn
                            ? new Date(account.createdOn).toLocaleDateString()
                            : "NA"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Users Count
                        </Typography>

                        <Link
                          to={`/home/${account.customerId}/users-list`}
                          style={{
                            textDecoration: "none",
                          }}
                        >
                          <Chip
                            label={account.usersCount}
                            clickable
                            variant="outlined"
                            color="primary"
                          />
                        </Link>

                        <GlassButtonLoading
                          loading={loggingIn}
                          startIcon={<LoginIcon />}
                          sx={{ ml: 2 }}
                          onClick={handleAccountLogin}
                        >
                          Account Login
                        </GlassButtonLoading>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* WHATSAPP BUSINESS ACCOUNT */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection
                    title="WhatsApp Business Account"
                    icon={<BusinessIcon />}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Phone Number ID
                        </Typography>

                        <Typography variant="body1" fontFamily="monospace">
                          {account.phoneNumberId || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Account ID
                        </Typography>

                        <Typography variant="body1" fontFamily="monospace">
                          {account.wabaAccountId || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          App ID
                        </Typography>

                        <Typography variant="body1" fontFamily="monospace">
                          {account.appId || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Quality
                        </Typography>

                        <Chip
                          label={account.wabaQuality || "UNKNOWN"}
                          color={getQualityColor(account.wabaQuality)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Display Phone Number
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" />

                          <Typography variant="body1" fontFamily="monospace">
                            {account.displayNumber || "—"}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Number Quality
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <SignalCellularAltIcon
                            fontSize="small"
                            color="action"
                          />

                          <Chip
                            label={account.numberQuality || "UNKNOWN"}
                            color={getQualityColor(account.numberQuality)}
                            size="small"
                          />
                        </Stack>

                        {showQualityBypass && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: account.bypassNumberQuality
                                ? "warning.light"
                                : "divider",
                              bgcolor: account.bypassNumberQuality
                                ? "rgba(245, 158, 11, 0.08)"
                                : "#f8fafc",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "text.primary",
                                  }}
                                >
                                  Bypass number quality
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    fontSize: 11.5,
                                    lineHeight: 1.45,
                                    color: "text.secondary",
                                  }}
                                >
                                  {account.bypassNumberQuality
                                    ? "Quality safeguards are bypassed for this account."
                                    : "Allow campaigns while this number has reduced quality."}
                                </Typography>
                              </Box>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                                sx={{ flexShrink: 0 }}
                              >
                                <Chip
                                  size="small"
                                  label={
                                    account.bypassNumberQuality ? "On" : "Off"
                                  }
                                  color={
                                    account.bypassNumberQuality
                                      ? "warning"
                                      : "default"
                                  }
                                  variant={
                                    account.bypassNumberQuality
                                      ? "filled"
                                      : "outlined"
                                  }
                                  sx={{ fontWeight: 700, minWidth: 42 }}
                                />
                                <Tooltip
                                  title={
                                    account.bypassNumberQuality
                                      ? "Turn quality bypass off"
                                      : "Turn quality bypass on"
                                  }
                                  arrow
                                >
                                  <Switch
                                    checked={
                                      account.bypassNumberQuality === true
                                    }
                                    disabled={updatingQualityBypass}
                                    onChange={(_, checked) =>
                                      handleQualityBypassChange(checked)
                                    }
                                    color="warning"
                                    inputProps={{
                                      "aria-label": "Bypass number quality",
                                    }}
                                  />
                                </Tooltip>
                              </Stack>
                            </Box>

                            {account.bypassNumberQuality && (
                              <Alert
                                severity="warning"
                                icon={false}
                                sx={{
                                  mt: 1.25,
                                  py: 0.25,
                                  px: 1,
                                  "& .MuiAlert-message": {
                                    p: 0,
                                    fontSize: 11.5,
                                    lineHeight: 1.45,
                                  },
                                }}
                              >
                                Use with care—sending heavily while quality is
                                reduced may further affect number health.
                              </Alert>
                            )}
                          </Box>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              gutterBottom
                            >
                              Unique Phone Numbers Reached
                            </Typography>

                            {isLoadingPhones ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                color="primary"
                              >
                                {uniquePhoneData?.totalCount ?? "—"}
                              </Typography>
                            )}
                          </Box>

                          <FormControl
                            size="small"
                            sx={{
                              minWidth: 140,
                            }}
                          >
                            <InputLabel>Period</InputLabel>

                            <Select
                              value={period}
                              label="Period"
                              onChange={(e) => setPeriod(e.target.value)}
                            >
                              <MenuItem value="last_month">Last Month</MenuItem>

                              <MenuItem value="last_year">Last Year</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* SECURITY & INTEGRATION */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection
                    title="Security & Integration"
                    icon={<SecurityIcon />}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Token
                        </Typography>

                        <SecretValue
                          label="WABA token"
                          value={account.wabaToken}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Linked Partner
                        </Typography>

                        <Chip
                          label={account.partner || "Not linked"}
                          variant="outlined"
                          color="primary"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS Partner
                        </Typography>

                        <Chip
                          label={account?.rcsPartner ?? ""}
                          variant="outlined"
                          color="primary"
                        />
                      </Grid>

                      {isAiSensyPartner && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              borderColor: "primary.light",
                              bgcolor: "rgba(25, 118, 210, 0.035)",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 2 }}
                            >
                              <BusinessIcon
                                color="primary"
                                sx={{ fontSize: 20 }}
                              />
                              <Typography variant="subtitle1" fontWeight={700}>
                                AiSensy Partner Details
                              </Typography>
                              <Chip
                                label="AiSensy"
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Stack>

                            <Grid container spacing={2}>
                              {[
                                {
                                  label: "Partner Business ID",
                                  value: account.partnerBusinessId,
                                },
                                {
                                  label: "Partner Project ID",
                                  value: account.partnerProjectId,
                                },
                              ].map(({ label, value }) => (
                                <Grid item xs={12} sm={6} key={label}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    {label}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 600,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {value || "—"}
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS User ID
                        </Typography>

                        <Typography variant="body1" fontFamily="monospace">
                          {account.rcsOptionBUserId || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS API Key
                        </Typography>

                        <SecretValue
                          label="RCS API key"
                          value={account.rcsOptionBapiKey}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS Bot ID
                        </Typography>

                        <Typography variant="body1" fontFamily="monospace">
                          {account.rcsBotId || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* WALLET BALANCES */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection
                    title="Wallet Balances"
                    icon={<AccountBalanceWalletIcon />}
                  >
                    <Stack spacing={2}>
                      {walletItems.map(
                        ({
                          label,
                          value,
                          color,
                          variant,
                          expiryDate,
                          allowSetExpiry,
                        }) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                {label}
                              </Typography>

                              <Chip
                                label={value}
                                color={color}
                                variant={variant}
                              />
                            </Box>

                            {expiryDate ? (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <CalendarDays
                                  size={14}
                                  color="rgba(0, 0, 0, 0.6)"
                                />

                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Expires on {formatExpiryDate(expiryDate)}
                                </Typography>
                              </Stack>
                            ) : allowSetExpiry ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: "warning.50",
                                  border: "1px solid",
                                  borderColor: "warning.light",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <CalendarDays size={14} />

                                  <Typography
                                    variant="caption"
                                    color="warning.dark"
                                    fontWeight={500}
                                  >
                                    No expiry date set
                                  </Typography>
                                </Stack>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={handleOpenRcsExpiryModal}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "auto",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Set Expiry
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        )
                      )}

                      {/* VOICE BAL LOCKED */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Voice Bal locked
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={`${account?.lockedVoiceCallWallet ?? 0}`}
                            color="info"
                            variant="outlined"
                          />

                          <Tooltip title="Release locked amount">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={
                                  !account?.lockedVoiceCallWallet ||
                                  Number(account.lockedVoiceCallWallet) <= 0
                                }
                                onClick={handleOpenReleaseLockModal}
                              >
                                <LockOpenIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>

                      {/* CONVERSION RATES */}
                      {(account?.voiceConversionRate != null ||
                        account?.rcsConversionRate != null) && (
                        <>
                          <Divider />

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              fontWeight={600}
                            >
                              Conversion Rates
                            </Typography>

                            <Tooltip title="Edit conversion rates">
                              <IconButton
                                size="small"
                                onClick={handleOpenRatesModal}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {account?.voiceConversionRate != null && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Voice Rate
                              </Typography>

                              <Chip
                                label={`${account.voiceConversionRate}x`}
                                color="warning"
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          )}

                          {account?.rcsConversionRate != null && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                RCS Rate
                              </Typography>

                              <Chip
                                label={`${account.rcsConversionRate}x`}
                                color="warning"
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          )}
                        </>
                      )}

                      {account?.voiceConversionRate == null &&
                        account?.rcsConversionRate == null && (
                          <>
                            <Divider />

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Conversion Rates
                              </Typography>

                              <Tooltip title="Set conversion rates">
                                <IconButton
                                  size="small"
                                  onClick={handleOpenRatesModal}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{
                                fontStyle: "italic",
                              }}
                            >
                              No conversion rates set
                            </Typography>
                          </>
                        )}

                      <Box sx={{ mt: 1 }}>
                        <Link
                          to={`/home/${id}/recharge-area`}
                          style={{
                            textDecoration: "none",
                          }}
                        >
                          <GlassButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Add Credit
                          </GlassButton>
                        </Link>
                      </Box>

                      <Box>
                        <Link
                          to={`/home/${id}/update-account-limit`}
                          style={{
                            textDecoration: "none",
                          }}
                        >
                          <GlassButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Set Phone Numbers Limit
                          </GlassButton>
                        </Link>
                      </Box>
                    </Stack>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* INTEGRATION ACTIONS */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection title="Integration Actions" icon={<LinkIcon />}>
                    <Stack spacing={2}>
                      <GlassButton
                        variant="contained"
                        fullWidth
                        startIcon={<FacebookIcon />}
                        onClick={() => launchEsSignup(true, account.customerId)}
                      >
                        Launch ES for Aisensy
                      </GlassButton>

                      <GlassButton
                        variant="contained"
                        fullWidth
                        startIcon={<FacebookIcon />}
                        onClick={() =>
                          launchEsSignup(false, account.customerId)
                        }
                      >
                        Launch ES for Haptik
                      </GlassButton>

                      <GlassButton
                        variant="contained"
                        fullWidth
                        onClick={handleOpenRCSModal}
                        startIcon={<Settings2Icon />}
                      >
                        Configure RCS Credentials
                      </GlassButton>

                      {/* GOOGLE SHEET INTEGRATION */}
                      <Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            fontWeight={600}
                            display="block"
                            gutterBottom
                          >
                            Google Sheet Integration
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {[
                              {
                                label: "WABA Chats",
                                key: "wabaChats",
                              },
                              {
                                label: "Voice Report",
                                key: "voiceReport",
                              },
                              {
                                label: "RCS Report",
                                key: "rcsReport",
                              },
                            ].map(({ label, key }) => (
                              <Chip
                                key={key}
                                label={label}
                                size="small"
                                color={
                                  account?.gSheet?.[key] ?? true
                                    ? "success"
                                    : "default"
                                }
                                variant={
                                  account?.gSheet?.[key] ?? true
                                    ? "filled"
                                    : "outlined"
                                }
                              />
                            ))}
                          </Stack>
                        </Paper>

                        <GlassButton
                          variant="contained"
                          fullWidth
                          startIcon={<TableChartIcon />}
                          onClick={handleOpenGSheetModal}
                        >
                          Update Google Sheet Integration
                        </GlassButton>
                      </Box>

                      {account?.partner === "haptik" &&
                        account?.onboardStatus === "success" && (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            Successfully onboarded to Haptik
                          </Alert>
                        )}

                      {account?.onboardStatus === "failed" && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          <Typography variant="subtitle2">
                            {account.hapTikError}
                          </Typography>

                          <Typography variant="body2">
                            {account.hapTikErrorDetails}
                          </Typography>
                        </Alert>
                      )}
                    </Stack>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* MIGRATION TOOLS */}
              <Card sx={sectionCardSx}>
                <CardContent>
                  <InfoSection title="Migration Tools">
                    <MigrateChatBot id={account.customerId} />
                  </InfoSection>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* RCS CREDENTIALS MODAL */}
      <Dialog
        open={openRCSModal}
        onClose={() => setOpenRCSModal(false)}
        fullWidth
      >
        <DialogTitle>Configure RCS</DialogTitle>

        <DialogContent>
          <Tabs
            value={rcsTab}
            onChange={(_, val) => setRcsTab(val)}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tab label="JIO" value={RCS_PARTNERS.JIO} />

            <Tab label="VIDEOCON" value={RCS_PARTNERS.VIDEOCON} />
          </Tabs>

          <TextField
            label={rcsTab === RCS_PARTNERS.JIO ? "RCS ID" : "RCS BOT ID"}
            fullWidth
            value={rcsBotId}
            onChange={(e) => setRcsBotId(e.target.value)}
            sx={{ my: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenRCSModal(false)}>Cancel</Button>

          <LoadingButton
            loading={submitting}
            onClick={handleRCSSubmit}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* RCS WALLET EXPIRY MODAL */}
      <Dialog
        open={openRcsExpiryModal}
        onClose={handleCloseRcsExpiryModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarDays size={20} />
          Set RCS Wallet Expiry
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            The RCS wallet currently has no expiry date. Select the expiry date
            to assign to this wallet.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Past, current and future dates are allowed.
          </Alert>

          <DatePicker
            label="Expiry Date"
            value={rcsExpiryDate}
            onChange={(newValue) => setRcsExpiryDate(newValue)}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                margin: "normal",
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseRcsExpiryModal}
            disabled={updatingRcsExpiry}
          >
            Cancel
          </Button>

          <LoadingButton
            loading={updatingRcsExpiry}
            disabled={!rcsExpiryDate || !dayjs(rcsExpiryDate).isValid()}
            onClick={handleUpdateRcsExpiry}
            variant="contained"
          >
            Update Expiry
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* CONVERSION RATES MODAL */}
      <Dialog
        open={openRatesModal}
        onClose={() => setOpenRatesModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Conversion Rates</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Leave a field empty to keep its current value unchanged. At least
            one rate must be provided.
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Voice Conversion Rate"
              type="number"
              inputProps={{
                step: "any",
                min: 0,
              }}
              fullWidth
              value={ratesForm.voiceConversionRate}
              onChange={(e) =>
                setRatesForm((f) => ({
                  ...f,
                  voiceConversionRate: e.target.value,
                }))
              }
              placeholder={
                account?.voiceConversionRate != null
                  ? String(account.voiceConversionRate)
                  : "e.g. 1.5"
              }
            />

            <TextField
              label="RCS Conversion Rate"
              type="number"
              inputProps={{
                step: "any",
                min: 0,
              }}
              fullWidth
              value={ratesForm.rcsConversionRate}
              onChange={(e) =>
                setRatesForm((f) => ({
                  ...f,
                  rcsConversionRate: e.target.value,
                }))
              }
              placeholder={
                account?.rcsConversionRate != null
                  ? String(account.rcsConversionRate)
                  : "e.g. 1.2"
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenRatesModal(false)}>Cancel</Button>

          <LoadingButton
            loading={updatingRates}
            onClick={handleRatesSubmit}
            variant="contained"
          >
            Update
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* GOOGLE SHEET INTEGRATION MODAL */}
      <Dialog
        open={openGSheetModal}
        onClose={() => setOpenGSheetModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TableChartIcon fontSize="small" color="success" />
          Google Sheet Integration
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enable or disable which data is synced to Google Sheets for this
            account.
          </Typography>

          <Stack spacing={1} sx={{ mt: 1 }}>
            {[
              {
                key: "wabaChats",
                label: "WABA Chats",
                caption: "Sync WhatsApp chat conversations to Google Sheets",
              },
              {
                key: "voiceReport",
                label: "Voice Report",
                caption: "Sync voice call reports to Google Sheets",
              },
              {
                key: "rcsReport",
                label: "RCS Report",
                caption: "Sync RCS reports to Google Sheets",
              },
            ].map(({ key, label, caption }) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={gSheetForm[key]}
                    onChange={(e) =>
                      setGSheetForm((f) => ({
                        ...f,
                        [key]: e.target.checked,
                      }))
                    }
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                    </Typography>

                    <Typography variant="caption" color="textSecondary">
                      {caption}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenGSheetModal(false)}>Cancel</Button>

          <LoadingButton
            loading={savingGSheet}
            onClick={handleGSheetSubmit}
            variant="contained"
            color="success"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* RELEASE LOCKED VOICE WALLET MODAL */}
      <Dialog
        open={openReleaseLockModal}
        onClose={() => setOpenReleaseLockModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LockOpenIcon fontSize="small" color="success" />
          Release Locked Voice Balance
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Currently locked amount:{" "}
            <strong>{account?.lockedVoiceCallWallet ?? 0}</strong>
          </Typography>

          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{
                fontSize: "0.85rem",
              }}
            >
              Choose release option
            </FormLabel>

            <RadioGroup
              value={releaseLockForm.mode}
              onChange={(e) =>
                setReleaseLockForm((f) => ({
                  ...f,
                  mode: e.target.value,
                }))
              }
            >
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={`Release full amount (${
                  account?.lockedVoiceCallWallet ?? 0
                })`}
              />

              <FormControlLabel
                value="custom"
                control={<Radio />}
                label="Enter amount to release"
              />
            </RadioGroup>
          </FormControl>

          {releaseLockForm.mode === "custom" && (
            <TextField
              label="Amount to release"
              type="number"
              inputProps={{
                step: "any",
                min: 0,
                max: account?.lockedVoiceCallWallet ?? undefined,
              }}
              fullWidth
              sx={{ mt: 2 }}
              value={releaseLockForm.amount}
              onChange={(e) =>
                setReleaseLockForm((f) => ({
                  ...f,
                  amount: e.target.value,
                }))
              }
              placeholder="e.g. 100"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenReleaseLockModal(false)}>Cancel</Button>

          <LoadingButton
            loading={releasingLock}
            onClick={handleReleaseLockSubmit}
            variant="contained"
            color="success"
          >
            Release
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountDetails;
